"use client"

import { useState, useEffect, useMemo, useCallback, useActionState } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { User, Lock, Mail, ArrowRight, Apple, Building, CreditCard, Globe, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Link from "next/link";
import { motion } from "motion/react";
import Image from "next/image";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { registerUser } from "@/app/actions/auth-actions";
import { formatPlanPrice, type SubscriptionPlan } from "@/lib/utils/plan-formatting";

const formSchema = z.discriminatedUnion('accountType', [
  z.object({
    accountType: z.literal('tenant'),
    subscriptionType: z.enum(["pay-as-you-go", "standard-monthly", "premium-monthly", "standard-yearly", "premium-yearly"], {
      required_error: "Please select a subscription plan"
    }),
    name: z.string().min(2, { message: "Name must be at least 2 characters" }),
    email: z.string().email({ message: "Please enter a valid email address" }),
    password: z.string().min(6, { message: "Password must be at least 6 characters" }),
    country: z.enum(["nigeria", "ghana"], {
      required_error: "Please select your country"
    }),
  }),
  z.object({
    accountType: z.literal('landlord'),
    subscriptionType: z.enum(["pay-as-you-go", "standard-monthly", "premium-monthly", "standard-yearly", "premium-yearly"]).optional(),
    name: z.string().min(2, { message: "Name must be at least 2 characters" }),
    phone: z.string().min(7, { message: "Please enter a valid phone number" }),
    email: z.string().email({ message: "Please enter a valid email address" }),
    password: z.string().min(6, { message: "Password must be at least 6 characters" }),
    country: z.enum(["nigeria", "ghana"], {
      required_error: "Please select your country"
    }),
  })
]);

const countryOptions = [
  { value: "nigeria", label: "Nigeria", flag: "/images/flags/nigeria.svg" },
  { value: "ghana", label: "Ghana", flag: "/images/flags/ghana.svg" }
];

const countryCodes = [
  { code: "+234", country: "nigeria" },
  { code: "+233", country: "ghana" },
];

type FormValues = z.infer<typeof formSchema>;

interface RegisterPageClientProps {
  initialPlanPrices: Record<string, number>
  subscriptionPlans: SubscriptionPlan[]
}

const initialState = {
  success: false,
  message: "",
  error: "",
  errors: {},
  data: null,
  requiresPayment: false,
  subscriptionType: "",
};

export default function RegisterPageClient({ 
  initialPlanPrices, 
  subscriptionPlans 
}: RegisterPageClientProps) {
  const [state, formAction, isPending] = useActionState(registerUser, initialState);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const router = useRouter();
  
  const planPrices = initialPlanPrices;
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (state.success && state.data) {
      if (state.requiresPayment && state.subscriptionType) {
        // Subscription plan - redirect to payment
        if (isMounted && typeof window !== 'undefined') {
          window.location.href = state.data.authorizationUrl;
        }
      } else {
        // Pay-as-you-go or landlord - redirect to success
        const email = state.data.email || '';
        const accountType = state.data.accountType || 'tenant';
        const plan = state.data.subscriptionType || 'pay-as-you-go';
        
        if (accountType === 'landlord') {
          router.push(`/register/success?email=${encodeURIComponent(email)}&type=landlord`);
        } else {
          const userId = state.data.userId || '';
          router.push(`/register/success?email=${encodeURIComponent(email)}&plan=${plan}&userId=${userId}`);
        }
      }
    }
  }, [state, router, isMounted]);

  useEffect(() => {
    if (state.message) {
      if (state.success) {
        toast.success(state.message);
      } else {
        toast.error(state.message);
      }
    }
  }, [state.message, state.success]);

  const defaultValues = {
    accountType: "tenant" as const,
    subscriptionType: "pay-as-you-go" as const,
    name: "",
    email: "",
    password: "",
    country: "nigeria" as const,
  };
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const accountType = form.watch("accountType");
  const selectedCountry = form.watch("country");

  const countryCode = useMemo(() => {
    if (accountType === "landlord") {
      const found = countryCodes.find(c => c.country === selectedCountry);
      return found?.code || countryCodes[0].code;
    }
    return countryCodes[0].code;
  }, [selectedCountry, accountType]);

  const handleAccountTypeChange = useCallback((newAccountType: "tenant" | "landlord") => {
    if (newAccountType === "landlord") {
      form.setValue("subscriptionType", undefined);
      if (typeof form.getValues("phone") === "undefined") {
        form.setValue("phone", "");
      }
    } else if (!form.getValues("subscriptionType")) {
      form.setValue("subscriptionType", "pay-as-you-go");
    }
  }, [form]);

  const handleSendOtp = useCallback(() => {
    const generatedOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setOtp(generatedOtp);
    setOtpSent(true);
    setOtpVerified(false);
    setOtpInput("");
    toast.success(`OTP sent: ${generatedOtp}`);
  }, []);

  const handleVerifyOtp = useCallback(() => {
    if (otpInput === otp) {
      setOtpVerified(true);
      toast.success("Phone number verified!");
    } else {
      toast.error("Invalid OTP. Please try again.");
    }
  }, [otpInput, otp]);

  return (
    <main className="min-h-[100vh] min-h-[100dvh] flex flex-col md:flex-row">
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full md:w-1/2 flex flex-col justify-center items-center p-8 md:p-16 bg-gradient-to-b from-[var(--color-neutral-50)] via-[var(--color-pure-white)] to-[var(--color-hover-overlay)]"
      >
        <div className="w-full max-w-md space-y-8">
          <div className="text-center md:text-left space-y-3">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-brand-primary-hover)] bg-clip-text text-transparent">
              Create an account
            </h1>
            <p className="text-[var(--color-neutral-600)]">Join RentWise today and start your journey</p>
          </div>
          
          <Form {...form}>
            <form action={formAction} className="space-y-6">
              {/* Hidden inputs to sync react-hook-form values with FormData for Server Actions */}
              <input type="hidden" name="accountType" value={form.watch("accountType")} />
              <input type="hidden" name="subscriptionType" value={form.watch("subscriptionType") || ""} />
              <input type="hidden" name="country" value={form.watch("country")} />
              
              <FormField
                control={form.control}
                name="accountType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[var(--color-neutral-700)]">Account Type</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                        handleAccountTypeChange(value as "tenant" | "landlord");
                      }} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] transition-colors">
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-[var(--color-pure-white)] border border-[var(--color-neutral-300)] shadow-lg z-50 backdrop-blur-sm">
                        <SelectItem value="tenant" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-2 text-[var(--color-tenant-primary)]" />
                            <span className="font-medium">As Tenant</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="landlord" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                          <div className="flex items-center">
                            <Building className="h-4 w-4 mr-2 text-[var(--color-landlord-primary)]" />
                            <span className="font-medium">As Landlord</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {accountType === "tenant" && (
                <FormField
                  control={form.control}
                  name="subscriptionType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[var(--color-neutral-700)]">Subscription Plan</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] transition-colors">
                            <SelectValue placeholder="Select subscription plan" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-w-[320px] bg-[var(--color-pure-white)] border border-[var(--color-neutral-300)] shadow-xl z-50 backdrop-blur-sm">
                          <SelectItem value="pay-as-you-go" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <CreditCard className="h-4 w-4 mr-2 text-[var(--color-brand-primary)]" />
                                <span className="font-medium">Pay-as-You-Go</span>
                              </div>
                              <span className="text-sm text-[var(--color-neutral-600)] ml-2">
                                {planPrices['pay-as-you-go'] ? formatPlanPrice(planPrices['pay-as-you-go']) + '/review' : 'Per review'}
                              </span>
                            </div>
                          </SelectItem>

                          <SelectItem value="standard-monthly" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <CreditCard className="h-4 w-4 mr-2 text-[var(--color-brand-primary)]" />
                                <span className="font-medium">Standard Monthly</span>
                              </div>
                              <span className="text-sm text-[var(--color-neutral-600)] ml-2">
                                {planPrices['standard-monthly'] ? formatPlanPrice(planPrices['standard-monthly']) + '/mo' : 'Monthly billing'}
                              </span>
                            </div>
                          </SelectItem>
                          <SelectItem value="premium-monthly" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <CreditCard className="h-4 w-4 mr-2 text-[var(--color-brand-primary)]" />
                                <span className="font-medium">Premium Monthly</span>
                              </div>
                              <span className="text-sm text-[var(--color-neutral-600)] ml-2">
                                {planPrices['premium-monthly'] ? formatPlanPrice(planPrices['premium-monthly']) + '/mo' : 'Monthly billing'}
                              </span>
                            </div>
                          </SelectItem>
                          <SelectItem value="standard-yearly" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <CreditCard className="h-4 w-4 mr-2 text-[var(--color-brand-primary)]" />
                                <span className="font-medium">Standard Yearly</span>
                              </div>
                              <span className="text-sm text-[var(--color-neutral-600)] ml-2">
                                {planPrices['standard-yearly'] ? formatPlanPrice(planPrices['standard-yearly']) + '/yr' : 'Annual billing'}
                              </span>
                            </div>
                          </SelectItem>
                          <SelectItem value="premium-yearly" className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <CreditCard className="h-4 w-4 mr-2 text-[var(--color-brand-primary)]" />
                                <span className="font-medium">Premium Yearly</span>
                              </div>
                              <span className="text-sm text-[var(--color-neutral-600)] ml-2">
                                {planPrices['premium-yearly'] ? formatPlanPrice(planPrices['premium-yearly']) + '/yr' : 'Annual billing'}
                              </span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-xs italic text-[var(--color-neutral-500)] mt-1 text-right">
                        {field.value === "pay-as-you-go" 
                          ? "No payment required now" 
                          : "Payment required after registration"}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[var(--color-neutral-700)]">Country</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] transition-colors">
                          <SelectValue placeholder="Select your country">
                            {field.value && (
                              <div className="flex items-center">
                                <div className="w-6 h-4 mr-2 relative overflow-hidden rounded-sm">
                                  <Image
                                    src={countryOptions.find(country => country.value === field.value)?.flag || ""}
                                    alt={field.value}
                                    fill
                                    className="object-cover"
                                  />
                                </div>
                                {countryOptions.find(country => country.value === field.value)?.label}
                              </div>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-[var(--color-pure-white)] border border-[var(--color-neutral-300)] shadow-lg z-50 backdrop-blur-sm">
                        {countryOptions.map(country => (
                          <SelectItem key={country.value} value={country.value} className="bg-[var(--color-pure-white)] hover:bg-[var(--color-neutral-100)] cursor-pointer">
                            <div className="flex items-center">
                              <div className="w-6 h-4 mr-2 relative overflow-hidden rounded-sm">
                                <Image
                                  src={country.flag}
                                  alt={country.label}
                                  fill
                                  className="object-cover"
                                />
                              </div>
                              {country.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[var(--color-neutral-700)]">Full Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                        <Input 
                          placeholder="John Smith" 
                          className="pl-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors" 
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {accountType === "landlord" && (
                <div>
                  <FormLabel className="text-[var(--color-neutral-700)] mb-1 block">Phone Number</FormLabel>
                  <div className="flex flex-row gap-2 items-end">
                    <div className="w-16">
                      <select
                        className="bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] rounded-md h-10 px-2 w-full"
                        value={countryCode}
                        disabled
                      >
                        {countryCodes.map(opt => (
                          <option key={opt.code} value={opt.code}>{opt.code}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-0">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="relative">
                                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                                <Input
                                  type="tel"
                                  placeholder="e.g. 8012345678"
                                  className="pl-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors"
                                  {...field}
                                  value={field.value}
                                  onChange={field.onChange}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <Button
                      type="button"
                      className="h-10 whitespace-nowrap"
                      onClick={handleSendOtp}
                      disabled={otpSent && !otpVerified}
                    >
                      {otpSent && !otpVerified ? "Resend OTP" : "Send OTP"}
                    </Button>
                  </div>
                  {otpSent && !otpVerified && (
                    <div className="flex gap-2 mt-2 items-center">
                      <Input
                        type="text"
                        placeholder="Enter OTP"
                        className="w-40"
                        value={otpInput}
                        onChange={e => setOtpInput(e.target.value)}
                        maxLength={6}
                      />
                      <Button type="button" onClick={handleVerifyOtp}>
                        Verify OTP
                      </Button>
                    </div>
                  )}
                  {otpVerified && (
                    <div className="text-[var(--color-cta-success)] text-sm mt-1">Phone number verified</div>
                  )}
                </div>
              )}
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[var(--color-neutral-700)]">Email</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                        <Input 
                          type="email" 
                          placeholder="name@example.com" 
                          className="pl-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors" 
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[var(--color-neutral-700)]">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-neutral-500)] h-4 w-4" />
                        <Input 
                          type={showPassword ? "text" : "password"} 
                          placeholder="••••••••" 
                          className="pl-10 pr-10 bg-[var(--color-neutral-50)] border-[var(--color-neutral-300)] hover:border-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] transition-colors" 
                          {...field} 
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isPending}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-[var(--color-neutral-500)]" />
                          ) : (
                            <Eye className="h-4 w-4 text-[var(--color-neutral-500)]" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-center my-4">
                <HCaptcha
                  sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY || "your-hcaptcha-site-key"}
                  onVerify={setCaptchaToken}
                />
              </div>
              
              {state.error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  {state.error}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white transition-colors"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Creating account...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Create account <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                )}
              </Button>
            </form>
          </Form>
          
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--color-neutral-300)]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gradient-to-b from-[var(--color-neutral-50)] via-[var(--color-pure-white)] to-[var(--color-hover-overlay)] text-[var(--color-neutral-500)]">
                  Or continue with
                </span>
              </div>
            </div>
            
            <div className="mt-6 grid grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="w-full border-[var(--color-neutral-300)] hover:bg-[var(--color-neutral-100)] hover:border-[var(--color-brand-primary)] transition-all"
              >
                <Apple className="h-5 w-5 mr-2" />
                Apple
              </Button>
              <Button 
                variant="outline" 
                className="w-full border-[var(--color-neutral-300)] hover:bg-[var(--color-neutral-100)] hover:border-[var(--color-brand-primary)] transition-all"
              >
                <svg className="h-5 w-5 mr-2" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.372 0 0 5.373 0 12s5.372 12 12 12c6.627 0 12-5.373 12-12S18.627 0 12 0zm.14 19.018c-3.868 0-7-3.14-7-7.018 0-3.878 3.132-7.018 7-7.018 1.89 0 3.47.697 4.675 1.85l-1.9 1.83c-.514-.497-1.415-1.07-2.775-1.07-2.377 0-4.317 1.97-4.317 4.408 0 2.438 1.94 4.408 4.317 4.408 2.756 0 3.793-1.977 3.95-3h-3.95v-2.417H17.1c.07.406.105.802.105 1.19 0 4.648-3.104 6.767-7.066 6.767z" />
                </svg>
                Google
              </Button>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-sm text-[var(--color-neutral-600)]">
              Already have an account?{" "}
              <Link 
                href="/login" 
                className="font-medium text-[var(--color-brand-primary)] hover:text-[var(--color-brand-primary-hover)] transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="hidden md:block w-1/2 bg-[var(--color-hover-overlay)] relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-brand-primary)]/20 to-[var(--color-brand-primary-hover)]/30 opacity-80"></div>
        
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/20 rounded-full animate-pulse"></div>
          <div className="absolute top-3/4 left-1/3 w-24 h-24 bg-white/20 rounded-full animate-pulse [animation-delay:1s]"></div>
          <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-white/20 rounded-full animate-pulse [animation-delay:2s]"></div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1/2">
            <svg className="absolute bottom-0 left-0 right-0" viewBox="0 0 1200 300" xmlns="http://www.w3.org/2000/svg">
              <path fill="rgba(255,255,255,0.1)" d="M0,224L48,218.7C96,213,192,203,288,208C384,213,480,235,576,234.7C672,235,768,213,864,213.3C960,213,1056,235,1152,229.3C1248,224,1344,192,1392,176L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
            
            <div className="absolute bottom-0 w-full flex justify-around">
              <div className="w-16 h-56 bg-white/10 rounded-t-lg"></div>
              <div className="w-12 h-40 bg-white/10 rounded-t-lg"></div>
              <div className="w-20 h-64 bg-white/10 rounded-t-lg"></div>
              <div className="w-14 h-48 bg-white/10 rounded-t-lg"></div>
              <div className="w-16 h-72 bg-white/10 rounded-t-lg"></div>
              <div className="w-10 h-36 bg-white/10 rounded-t-lg"></div>
              <div className="w-24 h-60 bg-white/10 rounded-t-lg"></div>
            </div>
          </div>
          
          <div className="absolute top-1/4 right-1/4 text-white/30 animate-bounce [animation-duration:3s]">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          
          <div className="absolute bottom-1/3 left-1/3 text-white/30 animate-bounce [animation-duration:4s] [animation-delay:1s]">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="p-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Start Your Journey</h2>
            <p className="text-xl text-white/80">Join our community and share your rental experiences</p>
          </div>
        </div>
      </motion.div>
    </main>
  );
} 